import { ActionTree } from 'vuex';
import { RootState } from '@/store/types';
import { BannerStandImagesState, BannerStandImagesActions, BannerStandImagesFilters } from './types';
import { BannerStandImagesMutations } from './types';
import BannerStandImage, { BannerStandImageStatus } from './banner-stand-image';
import { BannerStandImageJSON } from './banner-stand-image';
import { ListStatus, EditableStatus } from '@/store/types';
import { RootActions } from '@/store/types';
import { ErrorMessages } from '@/store/types';
import { enhanceErrorWithMessage } from '@/utils/errors';
import api from '@/api';
import router from '@/router';
import i18next from '@/i18n';
import { NotificationsActions } from '@/store/modules/notifications/types';

export const actions: ActionTree<BannerStandImagesState, RootState> = {
  [BannerStandImagesActions.LOAD_ALL]({ commit, dispatch }, filters = {}): Promise<void> {
    commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Loading);

    return api.bannerStandImages
      .list(filters)
      .then((response) => response.data)
      .then((response) => {
        const items = response.data.map((item: BannerStandImageJSON) => BannerStandImage.fromJSON(item));
        commit(BannerStandImagesMutations.SET_ALL, items);
        commit(BannerStandImagesMutations.SET_LIST_PAGINATION, {
          currentPage: response.current_page,
          lastPage: response.last_page,
          total: response.total,
        });
        commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.LOAD_EDITABLE]({ commit, dispatch }, { id }): Promise<void> {
    commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Loading);

    if (!id) {
      const item = new BannerStandImage();
      item.status = BannerStandImageStatus.Active;
      commit(BannerStandImagesMutations.SET_EDITABLE, item);
      commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      return Promise.resolve();
    }

    return api.bannerStandImages
      .get(id)
      .then((response) => response.data)
      .then((response) => {
        const item = BannerStandImage.fromJSON(response);
        commit(BannerStandImagesMutations.SET_EDITABLE, item);
        commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Loaded);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.UPDATE_EDITABLE]({ commit }, changes): Promise<void> {
    commit(BannerStandImagesMutations.UPDATE_EDITABLE, changes);
    return Promise.resolve();
  },

  [BannerStandImagesActions.DELETE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-banner-stand-image';
    if (isEdit) {
      commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.bannerStandImages.remove(id))
      .then(() => {
        if (isEdit) {
          commit(BannerStandImagesMutations.UPDATE_INITIAL_EDITABLE, {
            status: BannerStandImageStatus.Inactive,
          });
          commit(BannerStandImagesMutations.UPDATE_EDITABLE, {
            status: BannerStandImageStatus.Inactive,
          });
          commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(BannerStandImagesActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The banner stand image was deleted successfully.'),
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Failed);
        }
        
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.RESTORE]({ commit, dispatch }, { id = 0 }: { id?: number } = {}): Promise<void> {
    const isEdit = router.currentRoute.name === 'edit-banner-stand-image';
    if (isEdit) {
      commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);
    } else {
      commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Loading);
    }

    return new Promise<void>((resolve, reject) => {
      if (!id) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => api.bannerStandImages.restore(id))
      .then(() => {
        if (isEdit) {
          commit(BannerStandImagesMutations.UPDATE_INITIAL_EDITABLE, {
            status: BannerStandImageStatus.Active,
          });
          commit(BannerStandImagesMutations.UPDATE_EDITABLE, {
            status: BannerStandImageStatus.Active,
          });
          commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);
        } else {
          dispatch(BannerStandImagesActions.LOAD_ALL, {
            ...router.currentRoute.params,
            ...router.currentRoute.query,
          });
        }
      })
      .then(() =>
        dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: i18next.t('The banner stand image was restored successfully.'),
          },
          { root: true },
        ),
      )
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        if (isEdit) {
          commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        } else {
          commit(BannerStandImagesMutations.SET_ALL_STATUS, ListStatus.Failed);
        }

        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.APPLY_FILTERS]({ dispatch }, filters: BannerStandImagesFilters): Promise<void> {
    return dispatch(BannerStandImagesActions.LOAD_ALL, filters);
  },

  [BannerStandImagesActions.LOAD_PRODUCTS]({ commit, dispatch }): Promise<void> {
    return api.products
      .listTree()
      .then((response) => {
        commit(BannerStandImagesMutations.SET_PRODUCTS, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.LOAD_USED_PRODUCTS]({ commit, dispatch }, payload: { languageCode?: string } = {}): Promise<void> {
    const languageCode = payload.languageCode || 'en';
    return api.bannerStandImages
      .getUsedProducts({ language_code: languageCode })
      .then((response) => {
        commit(BannerStandImagesMutations.SET_USED_PRODUCTS, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.LOAD_EXISTING_LANGUAGES]({ commit, dispatch }, payload: { productId: number }): Promise<void> {
    return api.bannerStandImages
      .getExistingLanguages(payload.productId)
      .then((response) => {
        commit(BannerStandImagesMutations.SET_EXISTING_LANGUAGES, response.data);
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },

  [BannerStandImagesActions.SAVE]({ state, commit, dispatch }): Promise<void> {
    let isUpdate: boolean;
    let successMsg: string;

    return new Promise<void>((resolve, reject) => {
      if (!state.editable) {
        return reject(new Error('Invalid data!'));
      }

      resolve();
    })
      .then(() => {
        commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saving);

        isUpdate = Boolean(state.editable!.id);
        successMsg = i18next.t('A new banner stand image was added successfully.');

        if (isUpdate) {
          successMsg = i18next.t('The banner stand image was successfully updated.');
        }
      })
      .then(() => api.bannerStandImages.store(state.editable!.toJSON()))
      .then((response) => response.data)
      .then((response): Promise<BannerStandImage> => {
        const item = BannerStandImage.fromJSON(response);

        commit(BannerStandImagesMutations.SET_EDITABLE, item);
        commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Saved);

        if (!isUpdate) {
          return new Promise((resolve, reject) => {
            if (!item.id) {
              return reject(new Error('Invalid data.'));
            }

            router.replace(
              {
                name: 'edit-banner-stand-image',
                params: {
                  id: String(item.id),
                },
              },
              () => resolve(item),
              (error: any) => reject(error),
            );
          });
        }

        return Promise.resolve(item);
      })
      .then(() => {
        return dispatch(
          'notifications/' + NotificationsActions.SHOW_SUCCESS,
          {
            body: successMsg,
          },
          { root: true },
        );
      })
      .catch((error) => {
        enhanceErrorWithMessage(error, ErrorMessages.DATA_LOAD_ERROR);

        commit(BannerStandImagesMutations.SET_EDITABLE_STATUS, EditableStatus.Failed);
        return dispatch(RootActions.ERROR, error, { root: true });
      });
  },
};

